import type { RefCallback, RefObject } from "react";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type NavOverflow = {
  /** Box the nav row is allowed to occupy. Must be able to shrink (`min-w-0`). */
  setContainer: RefCallback<HTMLDivElement>;
  /**
   * Off-screen row holding one element per nav item at its natural width,
   * followed by the overflow trigger as its last child. Render it only while
   * `isMeasuring`.
   */
  setRuler: RefCallback<HTMLDivElement>;
  isMeasuring: boolean;
  visibleCount: number;
};

type Metrics = {
  itemWidths: number[];
  triggerWidth: number;
  gap: number;
};

const readColumnGap = (element: HTMLElement) =>
  Number.parseFloat(getComputedStyle(element).columnGap) || 0;

/**
 * How many items fit on one row, dropping them from the end backward. The
 * overflow trigger only takes up room once at least one item has dropped, so
 * the all-items-fit case is checked before any space is reserved for it.
 */
export const countItemsThatFit = ({
  available,
  itemWidths,
  triggerWidth,
  gap,
}: Metrics & { available: number }): number => {
  const fullWidth = itemWidths.reduce(
    (total, width, index) => total + width + (index === 0 ? 0 : gap),
    0,
  );

  if (fullWidth <= available) return itemWidths.length;

  const reservedForTrigger = gap + triggerWidth;
  let used = 0;
  let fits = 0;

  for (const width of itemWidths) {
    const nextUsed = used + width + (fits === 0 ? 0 : gap);
    if (nextUsed + reservedForTrigger > available) break;
    used = nextUsed;
    fits += 1;
  }

  return fits;
};

/**
 * Watch `node` for size changes, returning the cleanup React 19 runs when the
 * element is replaced or removed.
 */
const observe = (
  nodeRef: RefObject<HTMLDivElement | null>,
  node: HTMLDivElement,
  onResize: () => void,
) => {
  nodeRef.current = node;

  const observer = new ResizeObserver(onResize);
  observer.observe(node);
  onResize();

  return () => {
    observer.disconnect();
    nodeRef.current = null;
  };
};

/**
 * How many of `itemCount` nav items fit on one row, and therefore which ones
 * belong in the overflow menu.
 *
 * Item widths come from an off-screen ruler rather than the rendered row: an
 * item already moved into the overflow menu is no longer in the row to measure,
 * so the row alone can never tell us when it would fit again. Those widths only
 * change when the labels or the font do, so the ruler is mounted for exactly
 * that one commit and resizing is pure arithmetic on the cached measurements.
 * Leaving it mounted would duplicate every nav label in the DOM.
 *
 * `measureKey` should change whenever the rendered labels do.
 */
export const useNavOverflow = (
  itemCount: number,
  measureKey: string,
): NavOverflow => {
  const container = useRef<HTMLDivElement | null>(null);
  const ruler = useRef<HTMLDivElement | null>(null);
  const metrics = useRef<Metrics | null>(null);
  const [measuredKey, setMeasuredKey] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const isMeasuring = measuredKey !== measureKey;

  const applyMetrics = useCallback(() => {
    if (!container.current || !metrics.current) return;

    setVisibleCount(
      countItemsThatFit({
        available: container.current.clientWidth,
        ...metrics.current,
      }),
    );
  }, []);

  useLayoutEffect(() => {
    if (!isMeasuring || !ruler.current) return;

    const measured = Array.from(ruler.current.children) as HTMLElement[];

    metrics.current = {
      itemWidths: measured.slice(0, -1).map((item) => item.offsetWidth),
      triggerWidth: measured[measured.length - 1]?.offsetWidth ?? 0,
      gap: readColumnGap(ruler.current),
    };

    setMeasuredKey(measureKey);
    applyMetrics();
  }, [isMeasuring, measureKey, applyMetrics]);

  // A webfont swapping in after the first measurement changes every label's
  // width without resizing anything the observer watches.
  useEffect(() => {
    if (!document.fonts) return;

    let current = true;
    void document.fonts.ready.then(() => {
      if (current) setMeasuredKey(null);
    });

    return () => {
      current = false;
    };
  }, []);

  // Callback refs rather than a ResizeObserver wired up once on mount: the
  // whole nav row unmounts when the header switches to its mobile layout and
  // comes back as a *new* DOM node on the way out. An observer attached on
  // mount would still be watching the detached node, leaving the row frozen at
  // the width it last measured.
  const setContainer = useCallback(
    (node: HTMLDivElement) => observe(container, node, applyMetrics),
    [applyMetrics],
  );

  const setRuler = useCallback((node: HTMLDivElement) => {
    ruler.current = node;
    return () => {
      ruler.current = null;
    };
  }, []);

  return { setContainer, setRuler, isMeasuring, visibleCount };
};
